import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { trackEvent, trackScreenView } from '../utils/analytics';

// Separate contexts for different concerns
const CorporateModeContext = createContext();
const ActiveScreenContext = createContext();
const PreloadingContext = createContext();
const SoundContext = createContext();
export const PHONE_AUDIO_VOLUME = 0.7; // unified phone audio volume

// Individual context hooks
export const useCorporateMode = () => {
  const context = useContext(CorporateModeContext);
  if (!context) {
    throw new Error('useCorporateMode must be used within a SiteProvider');
  }
  return context;
};

export const useActiveScreen = () => {
  const context = useContext(ActiveScreenContext);
  if (!context) {
    throw new Error('useActiveScreen must be used within a SiteProvider');
  }
  return context;
};

export const usePreloading = () => {
  const context = useContext(PreloadingContext);
  if (!context) {
    throw new Error('usePreloading must be used within a SiteProvider');
  }
  return context;
};

export const useSound = () => {
  const context = useContext(SoundContext);
  if (!context) {
    throw new Error('useSound must be used within a SiteProvider');
  }
  return context;
};

// Legacy hook for backward compatibility
export const useSiteContext = () => {
  const corporateMode = useCorporateMode();
  const activeScreen = useActiveScreen();
  const preloading = usePreloading();
  const sound = useSound();
  
  return {
    ...corporateMode,
    ...activeScreen,
    ...preloading,
    ...sound
  };
};

export const SiteProvider = ({ children }) => {
  // Initialize isCorporateMode, default to false (no localStorage persistence)
  const [isCorporateMode, setIsCorporateMode] = useState(false);
  const [activeScreen, setActiveScreenState] = useState(null); // null, 'phone', 'tv', 'news', or 'logo'
  const [isPreloading, setIsPreloading] = useState(true);
  
  // Initialize isSoundOn from localStorage immediately using lazy initializer
  const [isSoundOn, setIsSoundOn] = useState(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('isSoundOn');
      return stored !== null ? JSON.parse(stored) : true;
    }
    return true;
  });

  // Save isSoundOn to localStorage whenever it changes
  useEffect(() => {
    if (!isPreloading && typeof window !== 'undefined') {
      localStorage.setItem('isSoundOn', JSON.stringify(isSoundOn));
    }
  }, [isPreloading, isSoundOn]);

  const navigationSourceRef = useRef('initial_load');
  const previousScreenRef = useRef(null);

  const toggleCorporateMode = useCallback(() => {
    setIsCorporateMode(prev => {
      const next = !prev;
      trackEvent('corporate_mode_toggle', {
        event_category: 'engagement',
        event_label: next ? 'on' : 'off'
      });
      return next;
    });
  }, [setIsCorporateMode]);

  const toggleSound = useCallback(() => {
    setIsSoundOn(prev => {
      const next = !prev;
      trackEvent('sound_toggle', {
        event_category: 'engagement',
        event_label: next ? 'sound_on' : 'sound_off'
      });
      return next;
    });
  }, [setIsSoundOn]);

  const setActiveScreen = useCallback((value, options = {}) => {
    const { source = 'direct' } = options;
    navigationSourceRef.current = source;

    setActiveScreenState(prev => {
      const next = typeof value === 'function' ? value(prev) : value;
      return next;
    });
  }, [setActiveScreenState]);

  const setScreen = useCallback((screen, options = {}) => {
    const { source = 'menu_icon' } = options;
    setActiveScreen(prev => (prev === screen ? null : screen), { source });
  }, [setActiveScreen]);

  // Memoized context values for each concern
  const corporateModeValue = useMemo(() => ({
    isCorporateMode,
    setIsCorporateMode,
    toggleCorporateMode
  }), [isCorporateMode, toggleCorporateMode]);

  const activeScreenValue = useMemo(() => ({
    activeScreen,
    setActiveScreen,
    setScreen
  }), [activeScreen, setScreen, setActiveScreen]);

  const preloadingValue = useMemo(() => ({
    isPreloading,
    setIsPreloading
  }), [isPreloading]);

  const soundValue = useMemo(() => ({
    isSoundOn,
    setIsSoundOn,
    toggleSound
  }), [isSoundOn, toggleSound]);

  useEffect(() => {
    const previousScreen = previousScreenRef.current ?? 'logo';
    const nextScreen = activeScreen ?? 'logo';

    if (previousScreenRef.current === activeScreen && previousScreenRef.current !== null) {
      return;
    }

    trackScreenView(nextScreen, {
      event_category: 'navigation',
      previous_screen: previousScreen,
      navigation_source: navigationSourceRef.current
    });

    previousScreenRef.current = activeScreen;
    navigationSourceRef.current = 'system';
  }, [activeScreen]);

  return (
    <CorporateModeContext.Provider value={corporateModeValue}>
      <ActiveScreenContext.Provider value={activeScreenValue}>
        <PreloadingContext.Provider value={preloadingValue}>
          <SoundContext.Provider value={soundValue}>
            {children}
          </SoundContext.Provider>
        </PreloadingContext.Provider>
      </ActiveScreenContext.Provider>
    </CorporateModeContext.Provider>
  );
};
